import { AnalyticsService } from 'base/analytics';
import { extractPaletteColor } from 'base/styleguide/color';
import { NEUTRAL_DARKEST } from 'base/styleguide/types';
import {
  Button,
  ButtonPalette,
  Options,
  RadioButtonGroup,
} from 'base/ui/button';
import {
  IconDelete,
  IconModeDark,
  IconModeLight,
  IconPng,
} from 'base/ui/icons';
import { Inline, Rows } from 'base/ui/layout';
import { LoadingDots } from 'base/ui/loading_dots';
import { menuItem } from 'base/ui/menu_model';
import { PanelLayout } from 'base/ui/panel';
import { SelectButton } from 'base/ui/select';
import { ToastQueue } from 'base/ui/toast';
import { COLOR_TOKENS } from 'base/ui/tokens';
import { TextSmall, TextSmallLight, TextTinyLight } from 'base/ui/typography';
import { useStableCallback } from 'base/useStableCallback';
import { ProjectSession } from 'blocks/editor/session/session';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { getUserJwt } from '../../../../library/src/modules/cookies';
import styles from './brand_panel.css';
import { ConceptsStore, getGlobalConceptsStore } from './concept_store';
import { SidebarNavigation } from './routes';

interface BrandPanelProps {
  session: ProjectSession;
  analytics: AnalyticsService;
  navigation: SidebarNavigation;
  toastQueue: ToastQueue;
}

const THEME_TOGGLE_OPTIONS: Options<'light' | 'dark'> = [
  {
    value: 'light',
    label: '',
    icon: IconModeLight,
    tooltip: 'Set logo in light mode',
  },
  {
    value: 'dark',
    label: '',
    icon: IconModeDark,
    tooltip: 'Set logo in dark mode',
  },
];

const LOGO_HEIGHT_OPTIONS = [28, 32, 36, 40, 44];

const PLACEHOLDER_LOGO = (
  <svg
    height="100%"
    viewBox="0 0 66 34"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M63.6292 16.1329L63.5941 16.168C63.8398 15.501 64.3312 15.2553 64.7524 15.2553C65.3492 15.2553 65.8757 15.7116 65.8757 16.3786C65.8757 16.519 65.8757 16.6945 65.8055 16.9051C64.5769 20.0642 61.9794 21.6789 59.4521 21.9246C58.2937 23.8903 56.3982 25.3295 53.6954 25.3295C49.8342 25.3295 48.1143 22.2756 48.1143 18.941C48.1143 14.8341 50.7118 10.2709 55.0644 10.2709C56.0121 10.2709 56.8195 10.4815 57.4864 10.7623C59.4521 11.4994 60.7158 14.0619 60.7158 16.7998C60.7158 17.6773 60.6456 18.5549 60.4349 19.3973C61.7337 18.941 62.9623 17.8879 63.6292 16.1329ZM56.2929 13.1492V13.1141C55.5207 13.1141 55.0644 14.1321 55.0644 15.3606C55.0644 17.1508 56.0472 18.8006 57.5917 19.3973C57.8374 18.6251 57.9427 17.7475 57.9427 16.7296C57.9427 14.7639 57.346 13.1492 56.2929 13.1492ZM53.7305 22.7671C54.7134 22.7671 55.6962 22.3458 56.4685 21.4683C54.1868 20.4504 52.7477 18.0634 52.7477 15.7818C52.7477 15.0096 52.8881 14.2023 53.0987 13.5002C51.6946 14.6586 50.8873 16.9402 50.8873 18.941C50.8873 21.5385 52.1158 22.7671 53.7305 22.7671Z"
      fill="currentcolor"
    />
    <path
      d="M48.6625 16.1332L48.6273 16.1683C48.8731 15.5014 49.2943 15.2206 49.7155 15.2206C50.3122 15.2206 50.9089 15.7471 50.9089 16.414C50.9089 16.5895 50.8738 16.7299 50.8036 16.9055C49.4347 20.2401 47.3988 24.1364 44.1343 26.418L44.0641 27.12C43.678 31.3322 41.5368 34 38.834 34C36.7981 34 35.6046 32.5959 35.6046 30.9461C35.6046 27.9625 38.6585 26.8743 41.5017 25.049C41.5719 24.3119 41.607 23.4695 41.6421 22.5217C40.238 24.0662 38.7287 24.698 37.3948 24.698C34.7271 24.698 32.5508 22.5217 32.5508 19.1871C32.5508 14.0622 35.9205 10.6925 39.6764 10.6925H39.7115C42.2739 10.6925 45.0119 12.0614 45.0119 14.5185C45.0119 15.3259 44.6609 19.7136 44.38 23.1185C46.2755 21.3283 47.8551 18.4148 48.6625 16.1332ZM37.7809 22.1707C39.0797 22.1707 40.8348 21.3634 41.9229 17.3969C42.0984 16.5193 42.2037 15.7471 42.1686 14.8345C41.958 13.8867 41.1507 13.3251 39.9923 13.3251C37.6054 13.3251 35.3238 15.6067 35.3238 19.0818C35.3238 21.1879 36.3067 22.1707 37.7809 22.1707ZM39.1148 31.4375H39.1499C39.887 31.4375 40.6944 30.9461 41.2209 27.787C39.6764 28.6996 38.2372 29.6123 38.2372 30.7004C38.2372 31.1567 38.5883 31.4375 39.1148 31.4375Z"
      fill="currentcolor"
    />
    <path
      d="M33.1585 16.1329L33.1234 16.168C33.3691 15.501 33.8605 15.2553 34.2817 15.2553C34.8785 15.2553 35.405 15.7116 35.405 16.3786C35.405 16.519 35.405 16.6945 35.3348 16.9051C34.1062 20.0642 31.5087 21.6789 28.9814 21.9246C27.823 23.8903 25.9276 25.3295 23.2247 25.3295C19.3635 25.3295 17.6436 22.2756 17.6436 18.941C17.6436 14.8341 20.2411 10.2709 24.5937 10.2709C25.5414 10.2709 26.3488 10.4815 27.0157 10.7623C28.9814 11.4994 30.2451 14.0619 30.2451 16.7998C30.2451 17.6773 30.1749 18.5549 29.9642 19.3973C31.263 18.941 32.4916 17.8879 33.1585 16.1329ZM25.8222 13.1492V13.1141C25.05 13.1141 24.5937 14.1321 24.5937 15.3606C24.5937 17.1508 25.5765 18.8006 27.121 19.3973C27.3667 18.6251 27.472 17.7475 27.472 16.7296C27.472 14.7639 26.8753 13.1492 25.8222 13.1492ZM23.2598 22.7671C24.2427 22.7671 25.2255 22.3458 25.9978 21.4683C23.7161 20.4504 22.277 18.0634 22.277 15.7818C22.277 15.0096 22.4174 14.2023 22.628 13.5002C21.2239 14.6586 20.4166 16.9402 20.4166 18.941C20.4166 21.5385 21.6451 22.7671 23.2598 22.7671Z"
      fill="currentcolor"
    />
    <path
      d="M19.4308 15.0238C18.9394 15.0238 18.5181 15.2695 18.2373 15.9365C17.3247 18.2532 15.4291 22.1847 13.4985 22.1847C12.2828 22.1847 11.3421 21.9097 10.3915 21.6319C9.42071 21.3481 8.43965 21.0614 7.14498 21.0614C6.68865 21.0614 6.0568 21.1316 5.46006 21.2369C7.25781 18.7854 7.92941 15.8165 8.58018 10.2046C7.35485 10.1266 6.35813 9.90028 5.63162 9.6716C4.85565 16.7891 3.97447 19.5029 0.756351 22.1847C0.335123 22.5357 0.124512 23.0271 0.124512 23.5186C0.124512 24.2908 0.791459 24.9578 1.63392 24.9578C1.91473 24.9578 2.23065 24.8525 2.54657 24.712C4.33679 23.9047 5.42496 23.6941 6.75885 23.6941C7.60708 23.6941 8.62208 23.9366 9.70873 24.1963C10.9605 24.4955 12.3073 24.8174 13.6038 24.8174C16.4471 24.8174 18.3075 22.0443 20.3786 16.6736C20.4839 16.4981 20.519 16.2875 20.519 16.112C20.519 15.445 19.9924 15.0238 19.4308 15.0238Z"
      fill="currentcolor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M5.80985 8.219C6.44551 8.44091 7.4379 8.69926 8.72459 8.77568L8.9352 8.77558C12.9018 8.77558 15.4993 6.31842 15.4993 3.36982C15.4993 1.4743 14.025 0 11.954 0C9.18091 0 7.25029 1.89552 6.26742 5.65147C5.03884 4.98453 4.19639 3.79105 3.77516 2.28165C3.56454 1.5445 3.10821 1.08817 2.47637 1.08817C1.70412 1.08817 1.21268 1.68491 1.21268 2.49227C1.21268 4.87923 3.07311 7.16088 5.81109 8.21395C5.81067 8.21563 5.81026 8.21732 5.80985 8.219ZM9.14581 6.21311C9.67235 3.79105 10.5148 2.63267 11.6381 2.63267C12.2348 2.63267 12.6209 2.9837 12.6209 3.61554C12.6209 4.77392 11.3924 6.14291 9.14581 6.21311Z"
      fill="currentcolor"
    />
  </svg>
);

function logoHeightLabel(height: number) {
  let rem = (0.063 * height).toFixed(1);
  if (rem.includes('.0')) {
    rem = rem.split('.')[0];
  }
  return `${rem}rem`;
}

function logoHeightSubLabel(height: number) {
  return `${height}px`;
}

const useLogo = (
  session: ProjectSession,
  conceptsStore: ConceptsStore,
  analytics: AnalyticsService,
  toastQueue: ToastQueue,
  theme: 'light' | 'dark',
): (
  | { state: 'empty' }
  | { state: 'loaded'; src: string }
  | { state: 'loading' }
) & {
  onUpload: (acceptedFiles: File[]) => Promise<void>;
} => {
  const [uploading, setUploading] = useState(false);

  const onUpload = useStableCallback(async (acceptedFiles: File[]) => {
    const isLoggedIn = !!getUserJwt();
    if (!isLoggedIn) {
      return session.showAuthWall?.();
    }

    if (acceptedFiles.length === 0) {
      return;
    }

    setUploading(true);
    try {
      await conceptsStore.uploadLogo(acceptedFiles[0], theme);
      analytics.track({
        event: 'project:concept_logo_update',
        theme,
      });
    } catch (err) {
      toastQueue.push({
        label: 'Could not upload logo',
      });
    } finally {
      setUploading(false);
    }
  });

  const cachedRenderedImage = conceptsStore.getRenderedLogo(theme);

  const ret = { onUpload };

  if (uploading || cachedRenderedImage?.type === 'pending') {
    return { state: 'loading', ...ret };
  }

  if (cachedRenderedImage?.value?.src) {
    return { state: 'loaded', src: cachedRenderedImage?.value?.src, ...ret };
  }

  return { state: 'empty', ...ret };
};

export const BrandPanel = observer(function _BrandPanel({
  session,
  analytics,
  navigation,
  toastQueue,
}: BrandPanelProps) {
  const [theme, setTheme] = useState<'light' | 'dark'>('light');

  const conceptStore = getGlobalConceptsStore();

  const logo = useLogo(session, conceptStore, analytics, toastQueue, theme);

  const activeConcept = conceptStore.activeConcept;

  const neutralDarkestHex = extractPaletteColor(activeConcept, NEUTRAL_DARKEST);

  const logoHeight = conceptStore.logo?.height ?? 32;

  const colorVars: Record<string, string> = {
    '--background-light':
      logo.state !== 'loaded'
        ? COLOR_TOKENS.colorLightGrey3
        : COLOR_TOKENS.colorWhite,
    '--foreground-light': COLOR_TOKENS.colorWhite,
    '--text-light': neutralDarkestHex,

    '--background-dark': neutralDarkestHex,
    '--foreground-dark': neutralDarkestHex,
    '--text-dark': COLOR_TOKENS.colorWhite,
  };

  const renderCurrentLogo = useCallback(
    (useUserSpecifiedHeight = true) => {
      const height = useUserSpecifiedHeight ? logoHeight + 'px' : undefined;
      if (logo.state !== 'loaded') {
        return (
          <div
            style={{
              height: height ?? '32px',
              opacity: !useUserSpecifiedHeight ? 0.2 : undefined,
            }}
          >
            {PLACEHOLDER_LOGO}
          </div>
        );
      }

      return <img style={{ height }} src={logo.src} />;
    },
    [logo, logoHeight],
  );

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'image/jpeg': [],
      'image/gif': [],
      'image/png': [],
      'image/webp': [],
    },
    onDrop: logo.onUpload,
  });

  return (
    <PanelLayout title="Brand" navigation={navigation}>
      <div style={colorVars} className={styles[theme]}>
        <Rows spacing={16}>
          <div className={styles.navPreview}>
            <nav>
              {renderCurrentLogo()}
              <a>Link One</a>
              <a>Link Two</a>
            </nav>
          </div>
          <ButtonPalette>
            <RadioButtonGroup
              options={THEME_TOGGLE_OPTIONS}
              value={theme}
              onChange={(v) => setTheme(v)}
            />
          </ButtonPalette>
          <Rows spacing={4}>
            <TextSmall>Logo Asset</TextSmall>
            <TextTinyLight color="grey">
              We suggest uploading your logo as a 2x PNG for best results.
            </TextTinyLight>
          </Rows>
          <button
            className={classNames(styles.logoUploadButton)}
            {...(logo.state === 'empty' ? getRootProps() : {})}
          >
            {logo.state === 'loading' ? (
              <div
                className={styles.loadingOverlay}
                style={{ color: theme === 'light' ? 'black' : 'white' }}
              >
                <LoadingDots color="inherit" aspectRatio={2 / 1} />
              </div>
            ) : (
              <>
                {logo.state === 'loaded' && <div className={styles.border} />}
                {renderCurrentLogo(false)}
                <div className={styles.uploadOverlay}>
                  {logo.state === 'loaded' ? (
                    <Button
                      label="Delete"
                      variant="tertiary"
                      background="white"
                      onClick={(e) => {
                        conceptStore.deleteLogo(theme);
                        analytics.track({
                          event: 'project:concept_logo_delete',
                          theme,
                        });
                        e.stopPropagation();
                      }}
                      icon={IconDelete}
                    />
                  ) : (
                    <>
                      <Button
                        label="Upload"
                        variant="tertiary"
                        background="white"
                        icon={IconPng}
                      />
                      <input {...getInputProps()} />
                    </>
                  )}
                </div>
              </>
            )}
          </button>
          <Inline xalign="space-between" yalign="center">
            <TextSmallLight color="grey">Logo height</TextSmallLight>
            <SelectButton
              size="medium"
              weight="light"
              menu={LOGO_HEIGHT_OPTIONS.map((size) =>
                menuItem({
                  label: logoHeightLabel(size),
                  subLabel: logoHeightSubLabel(size),
                  onClick: () => {
                    conceptStore.setLogoHeight(size);
                    analytics.track({
                      event: 'project:concept_logo_height_update',
                      height: size,
                    });
                  },
                  checked: size === logoHeight,
                }),
              )}
            />
          </Inline>
        </Rows>
      </div>
    </PanelLayout>
  );
});
